import type { Mention } from "@/shared/types/mention";
import { polarityTone } from "@/shared/types/polarity";

/**
 * Em que situação está a nota CENA de uma menção — a pergunta que o selo do
 * card, o resumo do "Mais detalhes" e o painel do editor faziam cada um com
 * as suas condições. Uma função só, pra os três não divergirem (2026-10-22).
 *
 * - `hidden`: a empresa esconde o CENA (`displays_cena` falso); nada aparece.
 * - `pending`: post de REDE sem nota fechada. O score depende de percentil
 *   sobre a janela do período e é calculado no relatório. A condição olha a
 *   ORIGEM do post, não a ausência de dado: o post de rede já tem os
 *   componentes fixos gravados desde o upload.
 * - `unscored`: imprensa NEUTRA sem peso cadastrado — `escena` NULL sem ser
 *   "pendente de relatório". Não é nota zero, e zero é outra coisa. O neutro
 *   vem do tom do DOMÍNIO de polaridade, nunca de uma string escrita à mão.
 * - `missing`: imprensa sem nota por qualquer outro motivo.
 * - `scored`: tem nota (`value`, de 0 a 10).
 */
export type CenaState =
  | { kind: "hidden" }
  | { kind: "pending" }
  | { kind: "unscored" }
  | { kind: "missing" }
  | { kind: "scored"; value: number };

export function cenaState(mention: Mention, showsCena: boolean): CenaState {
  if (!showsCena) return { kind: "hidden" };
  if (mention.mediaType === "network" && mention.escena === null) return { kind: "pending" };
  if (
    mention.mediaType !== "network" &&
    mention.escena === null &&
    polarityTone(mention.polarity) === "neutral"
  ) {
    return { kind: "unscored" };
  }
  if (mention.cena === null) return { kind: "missing" };
  return { kind: "scored", value: mention.cena };
}

export type CenaTone = "negative" | "neutral" | "positive";

/** A faixa de cor da nota: abaixo de 4 é negativa, de 4 a 6 (inclusive) é
 *  neutra e acima de 6 é positiva. */
export function cenaTone(value: number): CenaTone {
  if (value < 4) return "negative";
  if (value > 6) return "positive";
  return "neutral";
}

/** A largura da barrinha (0 a 100%): a nota de 0 a 10, presa à faixa. */
export function cenaBarPercent(value: number): number {
  return Math.min(10, Math.max(0, value)) * 10;
}
